import { useEffect } from 'react'
import { SOCIAL_PLATFORMS } from '../lib/formatSocialStat'
import { SOCIAL_PLATFORM_ICONS } from './SocialPlatformIcons'
import { SOCIAL_METRIC_ICONS, SOCIAL_METRIC_ROWS } from './SocialMetricIcons'
import AnimatedSocialStat from './AnimatedSocialStat'

const getMetricRawValue = (platformStats, key) => {
  if (!platformStats || platformStats.removed) return null

  const value = platformStats[key]
  if (value == null || value === '') return null

  const number = Number(value)
  if (!Number.isFinite(number)) return null

  return number
}

/**
 * Portrait panels sit just right of the centre card and shrink with it: the
 * panel keeps its 17rem layout width and is scaled as a whole, so text, icons,
 * padding and the scroll box all recede together instead of reflowing.
 * The 2.625rem gap folds together the old 0.75rem gap and 30px nudge, and it
 * scales too, which keeps the panel visually attached to the card.
 */
const PORTRAIT_PLACEMENT = {
  top: '50%',
  left: 'calc(50% + var(--carousel-card-w) / 2 + 2.625rem * var(--carousel-scale))',
  width: '17rem',
  transform: 'translateY(-50%) scale(var(--carousel-scale))',
  transformOrigin: 'left center',
}

const CarouselSocialStatsPanel = ({
  panelId,
  open,
  onClose,
  stats = {},
  videoLabel = '',
  isLandscape = false,
  frameStyle,
}) => {
  useEffect(() => {
    if (!open) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      id={panelId}
      role="dialog"
      aria-label={`Social stats for ${videoLabel || 'current video'}`}
      className={[
        'carousel-stats-panel pointer-events-auto absolute z-30 flex flex-col overflow-hidden rounded-xl',
        !frameStyle && isLandscape
          ? 'right-4 top-1/2 w-[17rem] max-w-[calc(100%-2rem)] translate-x-[30px] -translate-y-1/2'
          : '',
      ]
        .join(' ')
        .trimEnd()}
      style={frameStyle || (isLandscape ? undefined : PORTRAIT_PLACEMENT)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="carousel-stats-panel__header sticky top-0 z-[calc(var(--z-page)+10)] flex shrink-0 select-none items-center gap-1 px-3 py-2">
        <p className="carousel-stats-panel__title min-w-0 flex-1 truncate text-xs font-medium uppercase tracking-wide">
          stats
        </p>
        <button
          type="button"
          onClick={onClose}
          className="carousel-stats-panel__close grid h-5 w-5 shrink-0 place-items-center rounded text-[15px] font-bold leading-none"
          aria-label="Close stats panel"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <div className="max-h-[min(28rem,calc(100%-3rem))] space-y-4 overflow-y-auto overscroll-contain px-3 py-3">
        {videoLabel ? (
          <p className="carousel-stats-panel__subtitle truncate text-xs normal-case leading-snug">
            {videoLabel.toLowerCase()}
          </p>
        ) : null}

        <dl className="space-y-0">
          {SOCIAL_PLATFORMS.map(({ id, label }, platformIndex) => {
            const platformStats = stats?.[id]
            const PlatformIcon = SOCIAL_PLATFORM_ICONS[id]

            return (
              <div
                key={id}
                className="carousel-stats-panel__section space-y-2.5 pb-1 pt-5 first:border-t-0 first:pt-0"
              >
                <dt className="carousel-stats-panel__platform-label flex items-center gap-2.5 text-sm font-semibold uppercase tracking-wide">
                  {PlatformIcon ? (
                    <PlatformIcon className="carousel-stats-panel__platform-icon h-5 w-5" />
                  ) : null}
                  <span>{label}</span>
                </dt>
                <dd className="pl-7">
                  <ul className="space-y-2">
                    {SOCIAL_METRIC_ROWS.map(({ key, label: metricLabel }, metricIndex) => {
                      const MetricIcon = SOCIAL_METRIC_ICONS[key]
                      const rawValue = getMetricRawValue(platformStats, key)
                      const staggerDelayMs = platformIndex * 90 + metricIndex * 55

                      return (
                        <li key={key} className="flex items-center gap-2.5">
                          {MetricIcon ? (
                            <MetricIcon className="carousel-stats-panel__metric-icon h-4 w-4" />
                          ) : null}
                          <span className="carousel-stats-panel__metric-value min-w-[3.25rem] text-sm font-medium tabular-nums">
                            <AnimatedSocialStat
                              value={rawValue}
                              animateKey={videoLabel}
                              delayMs={staggerDelayMs}
                            />
                          </span>
                          <span className="carousel-stats-panel__metric-label text-xs normal-case">
                            {metricLabel}
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                </dd>
              </div>
            )
          })}
        </dl>
      </div>
    </div>
  )
}

export default CarouselSocialStatsPanel
